import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const CODE_LENGTH = 6;
const SGMA_API_URL = process.env.REACT_APP_SGMA_API_URL;

// Styling mirrors sgma_extract's WorkOSVerifyModal (MFA / Lost Password popup
// on the sign-in page) so password changes look and feel consistent across
// both apps, just rendered as a page instead of a popup.
const pageWrapper = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "60px 24px",
  background: "#f5f5f5",
};

const cardStyle = {
  background: "#fff",
  borderRadius: 14,
  padding: "28px 26px",
  width: "90vw",
  maxWidth: 380,
  boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
  textAlign: "center",
};

const iconBadgeStyle = {
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: "rgba(0,191,255,0.12)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px auto",
};

const titleStyle = {
  fontSize: 18,
  fontWeight: 700,
  marginBottom: 6,
  color: "#111",
};

const descriptionStyle = {
  fontSize: 13,
  color: "#666",
  marginBottom: 22,
  lineHeight: 1.5,
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: "#555",
  display: "block",
  textAlign: "left",
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  padding: "12px",
  borderRadius: "8px",
  border: "1px solid #ccc",
  boxSizing: "border-box",
  marginBottom: 16,
  fontSize: 14,
};

const otpRowStyle = {
  display: "flex",
  justifyContent: "center",
  gap: 8,
  marginBottom: 18,
};

const otpBoxStyle = {
  width: 42,
  height: 50,
  textAlign: "center",
  fontSize: 20,
  fontWeight: 700,
  borderRadius: 8,
  border: "1px solid #ccc",
  boxSizing: "border-box",
};

const primaryButton = {
  width: "100%",
  padding: "13px",
  borderRadius: "8px",
  background: "#00bfff",
  border: "none",
  color: "#fff",
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: 0.2,
  cursor: "pointer",
};

const linkButton = {
  display: "block",
  width: "100%",
  background: "none",
  border: "none",
  color: "#00bfff",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: 13,
  marginTop: 14,
  padding: 0,
  textAlign: "center",
};

const alertStyle = (isError) => ({
  fontSize: 13,
  marginBottom: 16,
  padding: "8px 10px",
  borderRadius: 6,
  background: isError ? "rgba(220,20,60,0.08)" : "rgba(0,135,79,0.08)",
  color: isError ? "crimson" : "#00874f",
});

function LockIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00bfff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00874f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function Header({ icon, title, description }) {
  return (
    <>
      <div style={iconBadgeStyle}>{icon}</div>
      <div style={titleStyle}>{title}</div>
      <div style={descriptionStyle}>{description}</div>
    </>
  );
}

export default function ChangePassword() {
  const navigate = useNavigate();
  const username = localStorage.getItem("spectraUser");

  // sendingCode -> code -> changePassword -> done
  const [step, setStep] = useState("sendingCode");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const codeRefs = useRef([]);

  const sendCode = async () => {
    setError("");
    setStep("sendingCode");

    if (!username) {
      setError("You must be signed in to change your password.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${SGMA_API_URL}/api/workos/send-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Could not send verification code.");
        return;
      }
      setStep("code");
    } catch (err) {
      console.error(err);
      setError("Could not reach the account server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    sendCode();
  }, []);

  const handleCodeDigitChange = (index, rawValue) => {
    const digit = rawValue.replace(/[^0-9]/g, "").slice(-1);
    const chars = code.split("");
    chars[index] = digit;
    const next = chars.join("").slice(0, CODE_LENGTH);
    setCode(next);
    if (digit && index < CODE_LENGTH - 1) {
      codeRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, CODE_LENGTH);
    if (!pasted) return;
    setCode(pasted);
    codeRefs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError("");

    if (code.length !== CODE_LENGTH) {
      setError("Enter the full 6-digit code.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${SGMA_API_URL}/api/workos/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Invalid or expired code.");
        return;
      }
      setResetToken(data.resetToken);
      setStep("changePassword");
    } catch (err) {
      console.error(err);
      setError("Could not reach the account server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${SGMA_API_URL}/api/workos/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resetToken, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Could not update password.");
        return;
      }
      setStep("done");
    } catch (err) {
      console.error(err);
      setError("Could not reach the account server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageWrapper}>
      <div style={cardStyle}>
        {step === "sendingCode" && !error && (
          <Header
            icon={<LockIcon />}
            title="Change Password"
            description="Sending a one-time verification code to the email on file for this account..."
          />
        )}

        {step === "code" && (
          <Header
            icon={<LockIcon />}
            title="Enter Verification Code"
            description={`A 6-digit code was sent to the email on file for "${username}". Enter it below to continue.`}
          />
        )}

        {step === "changePassword" && (
          <Header
            icon={<LockIcon />}
            title="Create New Password"
            description="Choose a new password for your Spectra account."
          />
        )}

        {step === "done" && (
          <Header
            icon={<CheckIcon />}
            title="Password Updated"
            description="Your password has been changed successfully."
          />
        )}

        {error && <div style={alertStyle(true)}>{error}</div>}

        {step === "sendingCode" && error && (
          <button type="button" style={primaryButton} onClick={sendCode} disabled={loading}>
            {loading ? "Sending..." : "Try Again"}
          </button>
        )}

        {step === "code" && (
          <form onSubmit={handleVerifyCode}>
            <div style={otpRowStyle} onPaste={handleCodePaste}>
              {Array.from({ length: CODE_LENGTH }).map((_, i) => (
                <input
                  key={i}
                  ref={(el) => (codeRefs.current[i] = el)}
                  value={code[i] || ""}
                  onChange={(e) => handleCodeDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  maxLength={1}
                  inputMode="numeric"
                  autoFocus={i === 0}
                  style={otpBoxStyle}
                />
              ))}
            </div>
            <button type="submit" style={primaryButton} disabled={loading}>
              {loading ? "Verifying..." : "Verify Code"}
            </button>
            <button type="button" style={linkButton} onClick={sendCode} disabled={loading}>
              Resend code
            </button>
          </form>
        )}

        {step === "changePassword" && (
          <form onSubmit={handleChangePassword}>
            <label style={labelStyle}>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={inputStyle}
              placeholder="New password"
              autoFocus
            />
            <label style={labelStyle}>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={inputStyle}
              placeholder="Confirm new password"
            />
            <button type="submit" style={primaryButton} disabled={loading}>
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        )}

        {step === "done" && (
          <button type="button" style={primaryButton} onClick={() => navigate("/settings")}>
            Back to Settings
          </button>
        )}
      </div>
    </div>
  );
}
